/**
 * active-editable-store.js — the ONE parent-side answer to "which editable is
 * selected right now".
 *
 * State only. No DOM, no eventBus and no policy — pick mode, staleness
 * and every other rule lives in `active-editable-controller.js`, which owns the
 * single write path. Deliberately the same split as
 * `core/selection-store.js` + `ui/selection-controller.js`, for the same reason:
 * it makes the model testable in node.
 *
 * ── Why this exists (CMD-0 finding) ───────────────────────────────────────
 * The authoritative value has always lived INSIDE the iframe (`activeEditable`
 * / `_selectedEditable` in `iframe-bridge-layers/message-handler.js`). On the
 * parent side there was no store at all: `glass-panels.js` (`_claimedClick`),
 * `ui/property-panel.js` (`_selectedInstance` / `_activeSvgData`) and
 * `glass-resize-handles.js` (`_activeEditableType`) each kept a PRIVATE copy,
 * derived independently from the raw `iframe:element-clicked` event.
 *
 * The store is now the shared identity source for the command bar and selection
 * services. A few editors still retain type-specific interaction state, but
 * they receive the same discriminated TargetRef rather than inventing identity.
 *
 * ── What it holds ─────────────────────────────────────────────────────────
 * The whole click payload, not a triple. Consumers need `href`, `target`,
 * `computed*` style metadata and the type-specific extras the iframe attaches
 * (see `iframe-bridge-layers/interaction.js`), and a store that kept only
 * {instanceId, eid, editableType} would force every consumer back onto the raw
 * event to get the rest — which is the problem this file exists to end.
 *
 * The payload is FROZEN on write and returned by reference. It is a snapshot of
 * a click; nothing may mutate it in place, because two consumers reading one
 * mutated snapshot is the same bug as two consumers keeping their own copy.
 */

import { targetRefFor, targetRefKey } from '../shared/target-ref.js';

/** Identity fields beside the discriminated target reference. */
const IDENTITY_KEYS = ['instanceId', 'editableType', 'kind'];

export class ActiveEditableStore {
  constructor() {
    /** @type {Readonly<object>|null} */
    this._payload = null;
    /** @type {Set<Function>} */
    this._subscribers = new Set();
    /** Reentrancy: writes issued from inside a notify are deferred. */
    this._notifying = false;
    /** @type {Array<Function>} */
    this._queue = [];
  }

  // ── reads ──────────────────────────────────────────────────────────────

  /** The frozen click payload, or null. Never mutate it. */
  get() {
    return this._payload;
  }

  /** @returns {string|null} owning section instance id */
  getInstanceId() {
    return this._payload?.instanceId ?? null;
  }

  /** @returns {string|null} editable id within the section */
  getEid() {
    return this._payload?.eid ?? null;
  }

  /** @returns {string|null} 'text' | 'image' | 'button' | 'svg' | 'logo' | … */
  getEditableType() {
    return this._payload?.editableType ?? null;
  }

  isEmpty() {
    return this._payload === null;
  }

  /** Is `payload` the same editable as the one held? Identity fields only. */
  isSame(payload) {
    if (!this._payload || !payload) return false;
    return (
      IDENTITY_KEYS.every((k) => this._payload[k] === payload[k]) &&
      targetRefKey(this._payload.targetRef) === targetRefKey(payload.targetRef)
    );
  }

  // ── writes (controller only) ───────────────────────────────────────────

  /**
   * @param {object} payload — the `iframe:element-clicked` payload. A payload
   *   without both `instanceId` and a valid TargetRef cannot identify an
   *   editable and CLEARS rather than storing a half-identity. DOM targets
   *   retain `eid`; section-CSS targets deliberately do not invent one.
   * @param {{source?: string, reason?: string}} [opts]
   * @returns {boolean} whether anything changed
   */
  set(payload, opts = {}) {
    const targetRef = targetRefFor(payload);
    if (!payload || !payload.instanceId || !targetRef) {
      return this.clear({ ...opts, reason: opts.reason || 'incomplete-payload' });
    }
    const eid = targetRef.domain === 'dom' ? targetRef.eid : null;
    return this._commit(Object.freeze({ ...payload, eid, targetRef }), opts);
  }

  /** @returns {boolean} whether anything changed */
  clear(opts = {}) {
    return this._commit(null, opts);
  }

  /**
   * @param {(change: {payload: object|null, previous: object|null,
   *   instanceId: string|null, eid: string|null, editableType: string|null,
   *   source?: string, reason?: string}) => void} fn
   * @returns {() => void} unsubscribe
   */
  subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    this._subscribers.add(fn);
    return () => this._subscribers.delete(fn);
  }

  // ── internals ──────────────────────────────────────────────────────────

  /**
   * The one place state changes. Commits BEFORE notifying, so a subscriber
   * reading get() during the notify sees the new value rather than depending on
   * subscriber registration order.
   */
  _commit(next, opts = {}) {
    if (this._notifying) {
      // A write from inside a notify is deferred until the loop drains, so two
      // notification sets can never interleave.
      this._queue.push(() => this._commit(next, opts));
      return false;
    }

    // Re-clicking the SAME editable is not a change. This matters: the iframe
    // re-sends element-clicked on every click, including repeat clicks on the
    // element already selected, and a store that notified on those would make
    // the command bar re-morph while the user is doing nothing.
    if (next === null && this._payload === null) return false;
    if (next !== null && this.isSame(next)) {
      // Identity unchanged but the metadata may be fresher (computed styles
      // after a restyle). Swap the snapshot silently — no notify, because no
      // consumer keyed on identity has anything to do.
      this._payload = next;
      return false;
    }

    const previous = this._payload;
    this._payload = next;

    const change = {
      payload: next,
      previous,
      instanceId: next?.instanceId ?? null,
      eid: next?.eid ?? null,
      editableType: next?.editableType ?? null,
      kind: next?.kind ?? null,
      source: opts.source,
      reason: opts.reason
    };

    this._notifying = true;
    try {
      for (const fn of [...this._subscribers]) {
        try {
          fn(change);
        } catch (err) {
          // One bad subscriber must not abort the rest or leave the store
          // mid-update — the state is already committed above.
          console.error('[active-editable-store] subscriber threw', err);
        }
      }
    } finally {
      this._notifying = false;
    }

    while (this._queue.length) {
      const nextWrite = this._queue.shift();
      nextWrite();
    }
    return true;
  }
}

/**
 * The singleton. One active editable per app, by definition.
 *
 * **Importing this binding means you intend to WRITE.** Only
 * `active-editable-controller.js` may — the single-write-path invariant, which
 * `command-bar-tests.js` (S1) enforces by inspecting import specifiers. An
 * import specifier cannot be aliased away: `import { activeEditable as x }`
 * still names `activeEditable`, which is what makes the check robust where a
 * grep for the local identifier was not.
 *
 * Readers import the accessors below instead.
 */
export const activeEditable = new ActiveEditableStore();

// ── read-only surface — safe for any module ─────────────────────────────────

/** The frozen payload of the active editable, or null. */
export function readActiveEditable() {
  return activeEditable.get();
}

/** @returns {{instanceId: string|null, eid: string|null, targetRef: object|null, editableType: string|null, kind: string|null}} */
export function readActiveEditableIdentity() {
  return {
    instanceId: activeEditable.getInstanceId(),
    eid: activeEditable.getEid(),
    targetRef: activeEditable.get()?.targetRef ?? null,
    editableType: activeEditable.getEditableType(),
    kind: activeEditable.get()?.kind ?? null
  };
}

/**
 * Subscribe to identity changes. Read-only: the callback receives the change
 * record, and there is no path from it back into a write.
 * @param {(change: object) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onActiveEditableChange(fn) {
  return activeEditable.subscribe(fn);
}
